/**
 * <vibe-gallery> — the full-screen shot viewer for a published vibe.
 *
 * Mobile: fullscreen horizontal swipe, one shot per view; the shot is
 * full-bleed at the top of each slide and the slide scrolls vertically to
 * reveal the module's body (caption, rail, set bar) below it.
 * Desktop: a two-column lightbox (shot left, panel right), one slide per
 * view; navigate via ‹ › arrows or ← → keys only (no coverflow peek).
 *
 * This element is a SHELL, not a renderer. Its slides are server-rendered by
 * sections/vibe-experience.liquid, each hosting a full <vibe-shot-module>
 * (snippets/vibe-shot-card.liquid): the hotspot dots are Liquid-positioned from
 * `frame_polygons` and assets/vibe-shop.js binds every behaviour — sheet,
 * shell-swap config, cart adds, wall hearts, analytics — to that exact DOM. So
 * the shell only ever ADDS its own chrome (close, progress dots, arrows) into
 * the existing frame; it never rebuilds or detaches the track. Moving the
 * track would disconnect/reconnect every module and tear down its wiring.
 * (The one sanctioned exception: the progress-dots element is re-parented into
 * the ACTIVE slide's `.vibe-shot__media` so the dots sit at the bottom of the
 * shot itself — an absolutely-positioned overlay, zero layout impact on the
 * media box, so the wrapper===image geometry contract holds.)
 *
 * The page shows compact `.vibe__shot-card` triggers; a tap on one opens this
 * viewer at that card's data-shot-index.
 */
class VibeGallery extends HTMLElement {
  connectedCallback() {
    this._built = false;
    this._lastFocus = null;
    this._onKey = (e) => {
      // The sheet (mobile) and the pickers' own option modals are top-layer
      // <dialog>s rendered ABOVE this overlay. While one is open it owns the
      // keyboard: Escape must close just the dialog (its own handler), not
      // collapse the whole viewer behind it, and arrows must not shuffle
      // slides underneath it. showModal also gives the dialog a native focus
      // trap, so ours must stand down.
      if (document.querySelector('dialog[open]')) return;
      if (e.key === 'Tab') { this._trapFocus(e); return; }
      // Desktop shell-swap: while a slide is in config state, Escape backs
      // out to the shot (via the module's own Back control — it runs the full
      // quick-view teardown) and arrows stand down: navigating would strand a
      // live sku-picker on an off-screen slide.
      const back = this.querySelector('.vibe-shot.is-config [data-config-back]');
      if (back) {
        if (e.key === 'Escape') back.click();
        return;
      }
      if (e.key === 'Escape') { this.close(); return; }
      if (e.key === 'ArrowLeft') { this._go(-1); return; }
      if (e.key === 'ArrowRight') { this._go(1); return; }
    };
    // i18n strings from data attributes (Liquid renders these from t: keys).
    this._t = { close: this.dataset.tClose || 'Close' };

    // Delegate card taps from the page. Bound property so it can be removed on teardown.
    this._onDocClick = (e) => {
      const card = e.target.closest('.vibe__shot-card');
      if (!card) return;
      this._lastFocus = card;
      this.open(Number(card.dataset.shotIndex) || 0);
    };
    document.addEventListener('click', this._onDocClick);

    // Deep link on load AND on later fragment navigations: navigating to the
    // same collection URL with a different `#shot=` is a SAME-document
    // navigation (no reload, connectedCallback doesn't re-run), so the cart
    // bundle's "view the look" link — and any in-page `#shot=` link — only
    // works via hashchange.
    this._onHashChange = () => this._openFromHash();
    window.addEventListener('hashchange', this._onHashChange);
    this._openFromLocation();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    window.removeEventListener('hashchange', this._onHashChange);
    document.removeEventListener('keydown', this._onKey);
    if (this._track && this._onScroll) this._track.removeEventListener('scroll', this._onScroll);
  }

  /**
   * Deep link: `/collections/<vibe>#shot=<mock_handle>` opens the viewer at
   * that shot (the cart's vibe-bundle "view the look" link builds exactly this).
   */
  _openFromHash() {
    const match = window.location.hash.match(/^#shot=(.+)$/);
    if (!match) return false;
    const handle = decodeURIComponent(match[1]);
    const slide = this.querySelector(`.vibe-gallery__slide[data-mock-handle="${CSS.escape(handle)}"]`);
    if (!slide) return false;
    this.open(Number(slide.dataset.shotIndex) || 0);
    return true;
  }

  /**
   * Land-in-viewer entry points, checked on load. The page with the film
   * hero + room-grouped trigger cards stays the DEFAULT — the viewer only
   * opens on load when explicitly requested:
   * - `#shot=<mock_handle>`  — the cart bundle's "view the look" deep link
   *   (wins over the query param when both are present);
   * - `?viewer=1`            — open at the first shot (ad landing URLs);
   * - `?viewer=<mock_handle>` — open at that shot; an unknown handle falls
   *   back to the first shot rather than dead-ending the landing.
   */
  _openFromLocation() {
    if (this._openFromHash()) return;
    let viewer = null;
    try { viewer = new URLSearchParams(window.location.search).get('viewer'); } catch (_) { /* no-op */ }
    if (!viewer) return;
    if (viewer === '1' || viewer === 'true') {
      this.open(0);
      return;
    }
    const slide = this.querySelector(`.vibe-gallery__slide[data-mock-handle="${CSS.escape(viewer)}"]`);
    this.open(slide ? Number(slide.dataset.shotIndex) || 0 : 0);
  }

  /**
   * Adopt the server-rendered frame/track and add the viewer chrome. Chrome is
   * APPENDED to the existing frame (never `replaceChildren`) so the track — and
   * with it every live <vibe-shot-module> — stays exactly where it was parsed.
   */
  _build() {
    const frame = this.querySelector('.vibe-gallery__frame');
    const track = frame && frame.querySelector('.vibe-gallery__track');
    // No server-rendered slides (a vibe with no mocks) — nothing to view.
    if (!frame || !track) return false;

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'vibe-gallery__close';
    close.setAttribute('aria-label', this._t.close);
    close.textContent = '✕';
    close.addEventListener('click', () => this.close());

    // Progress dots: one per slide. Skip for 0/1 shots (a single dot is noise).
    let dots = null;
    const count = track.children.length;
    if (count > 1) {
      dots = document.createElement('div');
      dots.className = 'vibe-gallery__dots';
      for (let i = 0; i < count; i++) {
        const dot = document.createElement('i');
        dot.className = 'vibe-gallery__dot';
        dots.appendChild(dot);
      }
    }
    this._dots = dots;

    // Desktop nav arrows (hidden on mobile via CSS).
    const nav = ['prev', 'next'].map((dir) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `vibe-gallery__nav vibe-gallery__nav--${dir}`;
      b.setAttribute('aria-label', dir === 'prev' ? 'Previous' : 'Next');
      b.textContent = dir === 'prev' ? '‹' : '›';
      b.addEventListener('click', () => this._go(dir === 'prev' ? -1 : 1));
      return b;
    });

    frame.append(close, ...nav);
    // The dots element is NOT appended to the frame: _setActive() parents it
    // into the active slide's media box (chrome on the shot, not the viewport).

    this._track = track;
    this._close = close;
    this._built = true;

    // Modal a11y: announce the overlay as a dialog.
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    this.setAttribute('aria-label', 'Vibe gallery');

    // Swipe-down to dismiss (mobile) — but each slide is now a vertical
    // scroll context (body content below the shot), so the gesture only arms
    // when it starts ON the shot image with the slide scrolled to the top.
    // Anywhere else a downward drag is a scroll, not a dismissal.
    let startY = null;
    track.addEventListener('touchstart', (e) => {
      const slide = e.target.closest('.vibe-gallery__slide');
      const onShot = e.target.closest('.vibe-shot__media');
      startY = slide && onShot && slide.scrollTop <= 0 ? e.touches[0].clientY : null;
    }, { passive: true });
    track.addEventListener('touchmove', (e) => {
      if (startY !== null && e.touches[0].clientY - startY > 90) { this.close(); startY = null; }
    }, { passive: true });

    // Track the centered slide on scroll → update dots + active (dim) state.
    this._onScroll = () => {
      const i = this._currentIndex();
      this._updateDots(i);
      this._setActive(i);
    };
    track.addEventListener('scroll', this._onScroll, { passive: true });
    return true;
  }

  _updateDots(index) {
    if (!this._dots) return;
    Array.from(this._dots.children).forEach((dot, i) => {
      dot.classList.toggle('is-on', i === index);
    });
  }

  // The slide whose centre is nearest the track's viewport centre.
  // Slides are full-width on both breakpoints now; kept centre-based so any
  // future slide sizing keeps working.
  _currentIndex() {
    if (!this._track) return 0;
    const center = this._track.scrollLeft + this._track.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    Array.from(this._track.children).forEach((sl, i) => {
      const c = sl.offsetLeft + sl.clientWidth / 2;
      const d = Math.abs(c - center);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    return best;
  }

  _centerOn(index, behavior) {
    const slide = this._track.children[index];
    if (!slide) return;
    const left = slide.offsetLeft - (this._track.clientWidth - slide.clientWidth) / 2;
    this._track.scrollTo({ left, behavior: behavior || 'auto' });
  }

  _setActive(index) {
    Array.from(this._track.children).forEach((sl, i) => sl.classList.toggle('is-active', i === index));
    // Progress dots are chrome ON THE SHOT: they live inside the active
    // slide's media box (absolute-positioned overlay at its bottom, clipped by
    // its overflow) so they ride the shot, not the viewport. Appending an
    // absolutely-positioned child adds no layout to the media box — the
    // wrapper===image geometry contract is untouched.
    if (this._dots) {
      const media = this._track.children[index]?.querySelector('.vibe-shot__media');
      if (media && this._dots.parentElement !== media) media.appendChild(this._dots);
    }
    // Single integration seam for "this slide is now active": any consumer
    // (e.g. the "more like this vibe" lazy-fetch grid in vibe-shop.js) listens
    // for this instead of re-deriving activeness from scroll/IO itself.
    // Bubbles to document so listeners don't need to be an ancestor of the
    // gallery in the DOM (they usually live INSIDE it, as a slide's module).
    if (this._lastActiveIndex !== index) {
      this._lastActiveIndex = index;
      this.dispatchEvent(new CustomEvent('vibe:slide-active', { detail: { index }, bubbles: true }));
    }
  }

  _go(dir) {
    const max = this._track.children.length - 1;
    const i = Math.min(max, Math.max(0, this._currentIndex() + dir));
    this._centerOn(i, 'smooth');
  }

  // Minimal focus trap: keep Tab focus within the overlay, wrapping at both ends.
  _trapFocus(e) {
    const focusables = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  open(index) {
    if (!this._built && !this._build()) return;
    this.hidden = false;
    document.body.style.overflow = 'hidden';
    document.removeEventListener('keydown', this._onKey);
    document.addEventListener('keydown', this._onKey);
    this._centerOn(index);
    this._setActive(index);
    this._updateDots(index);
    // Move focus into the overlay.
    this._close?.focus();
  }

  close() {
    // If a slide is mid-config (desktop shell swap), back out first: the Back
    // control runs the full quick-view teardown (destroy picker instance,
    // purge body/head collaborators, clear the global), so closing the viewer
    // can never strand a live sku-picker in a hidden slide.
    this.querySelector('.vibe-shot.is-config [data-config-back]')?.click();
    this.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._onKey);
    if (this._lastFocus) this._lastFocus.focus();
  }
}

if (!customElements.get('vibe-gallery')) {
  customElements.define('vibe-gallery', VibeGallery);
}
